import type { ServerMessage, StateMessage } from '../../shared/protocol';
import { CHAT_HISTORY_LIMIT } from '../../shared/config';
import { isTimedEffectKind } from '../../shared/power-ups';
import type { SnapshotState, WorkflowState } from './room-session-state';
import { decodeSolutionFilter } from '../../shared/solution-filter';

export interface SessionStateGroups {
  snapshot: SnapshotState;
  workflow: WorkflowState;
}

/** What the reducer noticed; the session performs the corresponding effects. */
export interface StateApplication {
  /** Words accepted by the server for this browser in the current round. */
  found: Map<string, number>;
  /** The archive retry is no longer relevant to the room's presented state. */
  cancelArchive: boolean;
  /** This browser's finished result, when the snapshot presents completed results. */
  ownResult: { roundId: string } | null;
}

/** Copy an authoritative snapshot onto the state groups. Pure: no sockets, storage or timers. */
export function applyStateMessage(
  { snapshot, workflow }: SessionStateGroups,
  message: StateMessage,
  now: number,
): StateApplication {
  const found = new Map(message.you.words.map((word) => [word.w, word.pts]));
  const cancelArchive =
    message.phase !== 'results' ||
    message.resultLifecycle.continuation !== 'archive-only' ||
    (snapshot.results !== null && message.results?.roundId !== snapshot.results.roundId);
  workflow.hasRoomInfo = true;
  Object.assign(snapshot, {
    resultLifecycle: message.resultLifecycle,
    mode: message.mode,
    dailyDate: message.dailyDate ?? null,
    spectator: message.you.spectator,
    finished: message.you.finished ?? false,
    begun: message.you.begun ?? false,
    phase: message.phase,
    round: message.round,
    roundId: message.currentRoundId,
    players: message.players,
    chat: message.chat,
    startAt: message.startAt,
    endAt: message.endAt,
    config: message.config,
    activeConfig: message.activeConfig,
    board: message.board,
    results: message.results,
    revealAt: message.revealAt ?? null,
    readyCheck: message.readyCheck ?? null,
    meId: message.you.id,
    found,
    powerUps: message.powerUps ?? [],
    effects: message.you.effects ?? [],
    shield: message.you.shield ?? false,
    solutionFilter: message.you.solutionFilter ? decodeSolutionFilter(message.you.solutionFilter) : null,
    endlessBoard: message.endlessBoard ?? null,
  });
  Object.assign(workflow, {
    // Restore presentation from durable room facts, not how this browser entered it.
    // A ranked match has no setup: its lobby only waits for both players to connect.
    inSetupLobby: message.mode !== 'versus' && message.mode !== 'competitive' && message.phase === 'lobby',
    ready: true,
    hasSnapshot: true,
    joinStatus: '',
    serverOffset: workflow.serverOffset || message.now - now,
  });
  const own =
    snapshot.phase === 'results'
      ? (snapshot.results?.players.find((player) => player.id === snapshot.meId) ?? null)
      : null;
  return {
    found,
    cancelArchive,
    ownResult: own && snapshot.results ? { roundId: snapshot.results.roundId } : null,
  };
}

/** Incremental messages that only touch the snapshot; returns false for messages needing effects. */
export function applyIncrementalMessage(snapshot: SnapshotState, message: ServerMessage): boolean {
  switch (message.t) {
    case 'players':
      snapshot.players = message.players;
      return true;
    case 'count':
      snapshot.players = snapshot.players.map((player) =>
        player.id === message.id ? { ...player, count: message.n } : player,
      );
      return true;
    case 'config':
      snapshot.config = message.config;
      return true;
    case 'chat':
      // Snapshots backfill history the live broadcast may also deliver.
      if (snapshot.chat.some((entry) => entry.id === message.message.id)) return true;
      snapshot.chat = [...snapshot.chat, message.message].slice(-CHAT_HISTORY_LIMIT);
      return true;
    case 'powerUp':
      applyPowerUp(snapshot, message);
      // App still narrates it, so this is not the whole handling.
      return false;
    default:
      return false;
  }
}

/** Mirror a consumed power-up: the spawn leaves the board and my own effects/shield follow the server. */
function applyPowerUp(snapshot: SnapshotState, message: Extract<ServerMessage, { t: 'powerUp' }>): void {
  if (message.roundId !== snapshot.roundId) return;
  snapshot.powerUps = snapshot.powerUps.map((spawn) =>
    spawn.id === message.id ? { ...spawn, takenBy: message.by } : spawn,
  );
  const me = snapshot.meId;
  if (message.shielded.includes(me)) snapshot.shield = false;
  if (!message.targets.includes(me)) return;
  if (message.kind === 'shield') snapshot.shield = true;
  if (isTimedEffectKind(message.kind) && message.until !== null) {
    const kind = message.kind;
    snapshot.effects = [
      ...snapshot.effects.filter((effect) => effect.kind !== kind),
      { kind, until: message.until },
    ];
  }
}

/** Record an accepted word once the server confirms it during play. */
export function recordAcceptedWord(
  snapshot: SnapshotState,
  message: Extract<ServerMessage, { t: 'wordResult' }>,
): void {
  if (message.ok && message.roundId === snapshot.roundId && snapshot.phase === 'playing')
    snapshot.found = new Map(snapshot.found).set(message.w, message.pts);
}

/** The state the room shows after this browser leaves it. */
export function resetForLeave({ snapshot, workflow }: SessionStateGroups): void {
  Object.assign(workflow, {
    inSetupLobby: false,
    creatingRoom: false,
    joiningRoom: false,
    archivePendingRoundId: null,
    connected: false,
    joined: false,
    ready: false,
    hasSnapshot: false,
    hasRoomInfo: false,
    autoStart: false,
    joinStatus: '',
  });
  Object.assign(snapshot, {
    archivedResult: null,
    phase: 'lobby',
    board: null,
    activeConfig: null,
    results: null,
    revealAt: null,
    readyCheck: null,
    players: [],
    chat: [],
    roundId: null,
    begun: false,
    solutionFilter: null,
  });
}
